<script setup lang="ts">
import type { ButtonPassThroughOptions, ButtonProps } from 'primevue/button';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import useQaAutoId from '@modules/core/qa-automation/utils/useQaAutoId';
import { useAddMerchantIdToRoute } from '@modules/merchant/composables';

import type {
  VButtonEmits,
  VButtonProps,
} from './types';

const props = withDefaults(defineProps<VButtonProps>(), {
  addMerchant: true,
  target: '_self',
});

const emit = defineEmits<VButtonEmits>();

const router = useRouter();
const { getQaAutoIdAttribute } = useQaAutoId();
const { addMerchantId } = useAddMerchantIdToRoute();

const className = computed<string>(() => {
  const classNames: string[] = [];

  if (props.textColor === 'white') {
    classNames.push('text-content-color hover:opacity-80 hover:bg-transparent');
  }

  return classNames.join(' ');
});

const buttonProps = computed<ButtonProps>(() => ({
  icon: props.icon ? `pi ${props.icon}` : undefined,
  loading: props.loading,
  size: props.size,
  raised: props.raised,
  label: props.label,
  outlined: props.outlined,
  rounded: props.rounded,
  text: props.text,
  class: className.value,
  severity: props.severity,
  disabled: props.loading || props.disabled,
  iconPos: props.iconPos,
}));

const href = computed<string | undefined>(() => {
  const routeTo = props.addMerchant && props.to ? addMerchantId(props.to) : props.to;

  return routeTo ? router.resolve(routeTo).href : props.href;
});

const ptButton = computed<ButtonPassThroughOptions>(() => ({
  root: {
    ...getQaAutoIdAttribute(),
  },
  label: {
    class: props.truncateLabel ? 'truncate' : '',
  },
}));

function emitClick(event: MouseEvent): void {
  emit('click', event);
}

function onNavigateClick(event: MouseEvent): void {
  if (props.to) {
    event.preventDefault();
  }

  if (props.disabled || props.loading) {
    return;
  }

  if (!props.prevented && props.to) {
    if (props.replace) {
      void router.replace(props.to);
    } else {
      void router.push(props.to);
    }
  }
  emitClick(event);
}
</script>

<template>
  <a v-data-qa-auto-id="'VButton'"
    v-if="href"
    :href="href"
    class="inline-block"
    :target="target"
    @click="onNavigateClick"
  >
    <PButton
      v-bind="buttonProps"
      :pt="ptButton"
    />
  </a>
  <PButton
    v-else
    v-bind="buttonProps"
    :pt="ptButton"
    @click="emitClick"
  />
</template>
